import { useState } from 'react';
import { Hint, Notice, PrimaryButton, Screen, SecondaryButton, Title } from './kit';

export type AuthMode = 'signin' | 'signup';

interface Props {
  mode: AuthMode;
  onModeChange: (mode: AuthMode) => void;
  onSubmit: (email: string, password: string) => void;
  onForgot: () => void;
  /** Родитель, у которого код уже есть, а подтверждать нечем: другое
   *  устройство, другой браузер, вкладку выгрузило. Получает введённый
   *  адрес, чтобы не набирать его заново. */
  onHaveCode: (email: string) => void;
  onPolicy: () => void;
  onLocalOnly: () => void;
  onTerms: () => void;
  busy?: boolean;
  error?: string | null;
  notice?: string | null;
}

const FIELD =
  'min-h-12 w-full rounded-xl border-2 border-line bg-card p-3 text-[1.05rem] text-ink';
const TAB = 'min-h-11 flex-1 rounded-lg border-2 p-2 font-semibold';

/** Вход и регистрация.
 *
 *  Поля обёрнуты классом `sentry-mask`. Маскировка в повторе сессии включена
 *  и по умолчанию, но здесь она держит пароль родителя, и полагаться на
 *  умолчание стороннего пакета в этом месте нельзя: смена умолчания в
 *  обновлении не сломает ни один тест и не даст ни одного сообщения — она
 *  просто начнёт писать пароли в чужой сервис.
 *
 *  Согласие — не формальность и не галочка «принимаю условия». Вся
 *  конструкция приложения держится на том, что учётная запись у взрослого, а
 *  дети — профили внутри неё: соглашается тот, чью почту и пароль вводят. */
export function AuthScreen({
  mode, onModeChange, onSubmit, onForgot, onHaveCode, onPolicy, onTerms, onLocalOnly, busy, error, notice,
}: Props) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [agreed, setAgreed] = useState(false);

  const signup = mode === 'signup';
  const ready = email.trim() !== '' && password !== '' && (!signup || agreed);

  return (
    <Screen>
      <Title>Тренажёр умножения</Title>
      <div className="flex gap-2">
        <button
          type="button"
          aria-pressed={!signup}
          onClick={() => onModeChange('signin')}
          className={`${TAB} ${!signup ? 'border-accent bg-accent-soft text-accent' : 'border-line bg-card text-ink'}`}
        >
          Вход
        </button>
        <button
          type="button"
          aria-pressed={signup}
          onClick={() => onModeChange('signup')}
          className={`${TAB} ${signup ? 'border-accent bg-accent-soft text-accent' : 'border-line bg-card text-ink'}`}
        >
          Регистрация
        </button>
      </div>

      <form
        className="sentry-mask flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          /** Второй рубеж к отключённой кнопке. Мутация `&&` → `||` здесь
           *  неразличима, и это проверено: кнопка отправки отключена при
           *  `!ready || busy`, а неявная отправка по Enter при отключённой
           *  кнопке по спецификации не происходит вовсе. Условие стоит
           *  потому, что кнопку однажды включат при `busy` ради
           *  «отзывчивости», и тогда двойное нажатие уедет двумя
           *  запросами. */
          if (ready && !busy) onSubmit(email.trim(), password);
        }}
      >
        <label className="flex flex-col gap-1 text-[0.95rem] text-muted">
          Почта родителя
          <input
            type="email"
            value={email}
            autoComplete="username"
            inputMode="email"
            required
            className={FIELD}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1 text-[0.95rem] text-muted">
          Пароль
          <input
            type="password"
            value={password}
            autoComplete={signup ? 'new-password' : 'current-password'}
            required
            minLength={signup ? 8 : undefined}
            className={FIELD}
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>

        {signup && (
          <>
            <Hint>Пароль — не короче восьми знаков.</Hint>
            <label className="flex items-start gap-2 text-[0.95rem]">
              <input
                type="checkbox"
                checked={agreed}
                className="mt-1 size-5"
                onChange={(event) => setAgreed(event.target.checked)}
              />
              <span>
                Я родитель или законный представитель детей, чьи профили заведу, и
                разрешаю обработку данных их занятий на условиях{' '}
                <button
                  type="button"
                  onClick={onPolicy}
                  className="underline"
                >
                  политики обработки данных
                </button>{' '}
                и{' '}
                <button
                  type="button"
                  onClick={onTerms}
                  className="underline"
                >
                  условий использования
                </button>
                .
              </span>
            </label>
          </>
        )}

        {/* Отказ относится к форме целиком, а не к одному полю: «неверная
            почта или пароль» не говорит, какое из двух виновато. Поэтому
            немедленное объявление без связи с полем. */}
        <Notice text={notice} />
        {error && <p role="alert" className="m-0 text-[0.95rem] text-incorrect">{error}</p>}

        <button
          type="submit"
          disabled={!ready || busy}
          className="mt-2 rounded-[0.85rem] bg-accent p-4 text-[1.1rem] font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#c7cddb]"
        >
          {signup ? 'Зарегистрироваться' : 'Войти'}
        </button>
      </form>

      <div className="mt-auto flex flex-col gap-1">
        {/* Дверь на экран ввода кода. Без неё родитель, потерявший этот
            экран, заперт: запись создана, письмо с кодом пришло, войти
            нельзя — почта не подтверждена, — а вводить код негде. */}
        <button
          type="button"
          onClick={() => onHaveCode(email.trim())}
          className="min-h-11 rounded-lg text-[0.95rem] text-muted underline"
        >
          У меня есть код из письма
        </button>
        <button
          type="button"
          onClick={onForgot}
          className="min-h-11 rounded-lg text-[0.95rem] text-muted underline"
        >
          Забыли пароль?
        </button>
        {/* Дверь мимо регистрации.
            Довод не в удобстве, а в том, кто стоит у экрана: ребёнок, которому
            обещали заниматься, и взрослый, которому вместо этого показали форму
            с почтой и паролем. Цена названа тут же и без обиняков — занятия
            остаются на одном устройстве, — потому что узнать её потом, потеряв
            полгода занятий, значительно дороже. */}
        <button
          type="button"
          onClick={onLocalOnly}
          className="min-h-11 rounded-lg text-[0.95rem] text-muted underline"
        >
          Заниматься без учётной записи
        </button>
        <p className="m-0 text-[0.8rem] text-muted">
          Без входа занятия хранятся только на этом устройстве и не попадут на второе.
          Войти можно потом — всё, что уже пройдено, сохранится.
        </p>
      </div>
    </Screen>
  );
}

/** Экран «войдите снова» после истечения сессии.
 *
 *  Отдельный от входа, потому что говорит другое: данные ребёнка на месте,
 *  просто доступ к ним надо подтвердить. Стирать здесь ничего нельзя —
 *  `SIGNED_OUT` приходит и при неудачном обновлении токена, то есть при
 *  плохой связи, а не только по нажатию «Выйти». */
export function SessionExpired(
  { onSignIn, onLocalOnly }: { onSignIn: () => void; onLocalOnly: (() => void) | null },
) {
  return (
    <Screen>
      <Title>Войдите снова</Title>
      <Hint>
        Сеанс закончился. Занятия на этом устройстве целы — чтобы продолжить,
        введите почту и пароль.
      </Hint>
      <PrimaryButton onClick={onSignIn}>Войти</PrimaryButton>
      {/* Вторая дверь — для случая, ради которого этот экран и появляется
          на холодном запуске: планшет в машине, токен протух за ночь, сети
          нет. Требовать вход означало бы «сегодня не занимаемся» при целом
          журнале на устройстве. Занятия, записанные так, уедут при
          следующем входе: устройство помнит своего владельца.

          Её НЕТ, когда сервер ответил отказом: доступ отозван, выход сделан
          на всех устройствах или запись удалена. Показать дверь там значило
          бы отменять решение взрослого соседней кнопкой. */}
      {onLocalOnly && (
        <SecondaryButton bottom onClick={onLocalOnly}>
          Заниматься без интернета
        </SecondaryButton>
      )}
    </Screen>
  );
}
